import { useState, useEffect, useMemo } from "react";
import { useRoute, useLocation, Link } from "wouter";
import { motion } from "framer-motion";
import {
  Package,
  Star,
  MapPin,
  Phone,
  Video,
  CheckCircle2,
  Clock,
  Award,
  Building2,
  Calendar as CalendarIcon,
  Mail,
  FileText,
  Activity,
  Heart,
  Share2,
  ShieldCheck,
  Sparkles,
  ChevronRight,
  ArrowLeft,
  Check,
  Copy,
  Info,
  CreditCard,
  RefreshCw,
  UserCheck,
  Stethoscope,
  ChevronDown,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarUI } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { useGetPublicBundle, useGetProvider, useBookAppointment, API_BASE } from "@/lib/api-client-react";
import { useToast } from "@/hooks/use-toast";

export default function BundleDetail() {
  const [match1, params1] = useRoute("/bundle/:id");
  const [match2, params2] = useRoute("/bundles/:id");
  const [match3, params3] = useRoute("/provider/:providerId/bundle/:bundleId");
  const id = (params3 as any)?.bundleId || (params1 as any)?.id || (params2 as any)?.id || "";
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const { data: bundle, isLoading, error } = useGetPublicBundle(id, { query: { enabled: !!id } });
  
  const mainProviderId = bundle?.providerInfo?.id || bundle?.providerInfo?.providerId || bundle?.providerId;
  const { data: mainProviderDetail } = useGetProvider(mainProviderId || "", {
    query: { enabled: !!mainProviderId }
  });

  const bookAppointment = useBookAppointment();

  const [user, setUser] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedTime, setSelectedTime] = useState<string>("09:00 AM");
  const [selectedGroupProvider, setSelectedGroupProvider] = useState<string>("any");
  const [patientNotes, setPatientNotes] = useState<string>("");
  const [copiedLink, setCopiedLink] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isSubscription = Boolean(bundle?.isSubscription);
  const recurringAmount = bundle?.recurringAmount ?? (bundle?.finalPrice ? bundle.finalPrice / 12 : 0);
  const intervalLabel = (bundle?.subscriptionInterval || 'MONTHLY').toLowerCase().replace('_', '-');

  useEffect(() => {
    const u = localStorage.getItem("user");
    if (u) {
      try {
        setUser(JSON.parse(u));
      } catch (e) {
        console.error("Error parsing user from localStorage", e);
      }
    }
  }, []);

  const getImageUrl = (url?: string | null) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    const baseUrl = API_BASE.replace(/\/v1.*$/, "");
    return `${baseUrl}${url.startsWith("/") ? "" : "/"}${url}`;
  };

  const handleCopyLink = () => {
    if (typeof window !== "undefined") {
      navigator.clipboard.writeText(window.location.href);
      setCopiedLink(true);
      toast({
        title: "Product Link Copied!",
        description: "Direct bundle link copied to your clipboard."
      });
      setTimeout(() => setCopiedLink(false), 3000);
    }
  };

  const availableProviders: { id: string; name: string; specialty?: string }[] = useMemo(() => {
    if (!bundle) return [];
    
    // Combine linked providers from bundle or fallback mainProviderDetail
    const linked = (bundle.providerInfo?.linkedProviders && bundle.providerInfo.linkedProviders.length > 0)
      ? bundle.providerInfo.linkedProviders
      : (mainProviderDetail?.linkedProviders || []);
      
    const pProviders = bundle.participatingProviders;

    // 1. If participatingProviders specifies providerIds, filter matching linked/explicit providers
    if (Array.isArray(pProviders?.providerIds) && pProviders.providerIds.length > 0) {
      const ids = pProviders.providerIds.map(String);
      const matched = linked.filter((lp: any) => ids.includes(String(lp.providerId || lp.id)));
      if (matched.length > 0) {
        return matched.map((lp: any) => ({
          id: String(lp.providerId || lp.id),
          name: lp.name || `${lp.user?.firstName || ''} ${lp.user?.lastName || ''}`.trim() || 'Doctor',
          specialty: lp.headline || lp.specialty
        }));
      }
    } else if (Array.isArray(pProviders) && pProviders.length > 0) {
      return pProviders.map((p: any) => ({
        id: String(p.providerId || p.id),
        name: p.name || (p.user ? `${p.user.firstName} ${p.user.lastName}` : (p.firstName ? `${p.firstName} ${p.lastName}` : `Doctor`)),
        specialty: p.headline || p.specialty
      }));
    }

    // 2. If practice has associated/linked providers (e.g. Unnati Desai in Desai Group), list all associated doctors!
    if (linked.length > 0) {
      return linked.map((lp: any) => ({
        id: String(lp.providerId || lp.id),
        name: lp.name || `${lp.user?.firstName || ''} ${lp.user?.lastName || ''}`.trim() || 'Doctor',
        specialty: lp.headline || lp.specialty
      }));
    }

    // 3. Fallback to main providerInfo
    if (bundle.providerInfo) {
      return [{
        id: String(bundle.providerInfo.id || bundle.providerId),
        name: bundle.providerInfo.name || 'Assigned Provider',
        specialty: bundle.providerInfo.specialty
      }];
    }

    return [];
  }, [bundle, mainProviderDetail]);

  const handleCheckout = async () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    if (!bundle) return;
    setIsSubmitting(true);

    try {
      const providerId = bundle.providerInfo?.providerId || bundle.providerId;
      const formattedDate = selectedDate.toISOString().split("T")[0];

      await bookAppointment.mutateAsync({
        providerId,
        bundleId: bundle.id,
        appointmentDate: formattedDate,
        startTime: selectedTime || "09:00 AM",
        notes: `[BUNDLE ${isSubscription ? "SUBSCRIPTION" : "ONE_TIME"}] ${patientNotes}`.trim(),
        groupId: selectedGroupProvider !== "any" ? selectedGroupProvider : undefined,
        amount: isSubscription ? (recurringAmount || bundle.finalPrice || 0) : (bundle.finalPrice || bundle.totalPrice || 0)
      });

      setShowSuccessModal(true);
      toast({
        title: isSubscription ? "Subscription Order Submitted!" : "Bundle Order Submitted!",
        description: `Your ${isSubscription ? "subscription request" : "checkout booking"} for ${bundle.name} has been processed.`
      });
    } catch (err: any) {
      console.error("Checkout error:", err);
      toast({
        variant: "destructive",
        title: "Checkout Failed",
        description: err.message || "Failed to complete checkout. Please try again."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const provider = bundle?.providerInfo;
  const gradient = "from-indigo-600 to-cyan-600";
  const initials = provider?.name
    ? provider.name.split(" ").filter(Boolean).map((n: string) => n[0]).slice(0, 2).join("")
    : "PR";

  const standardTotal = bundle?.totalPrice || 0;
  const finalPrice = bundle?.finalPrice ?? standardTotal;
  const discountPercent = bundle?.discountPercent || (standardTotal > finalPrice && standardTotal > 0 ? Math.round(((standardTotal - finalPrice) / standardTotal) * 100) : 0);
  const monthlySubscriptionPrice = Math.round((finalPrice / 3) * 100) / 100;

  const renderHeader = () => (
    <header className="fixed top-0 inset-x-0 z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-lg border-b border-slate-200 dark:border-slate-800">
      <div className="w-full max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link href="/">
          <div className="flex items-center gap-2 cursor-pointer">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center shadow-sm">
              <Heart className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-xl text-slate-900 dark:text-white tracking-tight">StraightDoc</span>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          {user ? (
            <Link href={user.role === "PROVIDER" || user.role === "CLINIC" ? "/provider/dashboard" : "/member/dashboard"}>
              <Button variant="outline" size="sm" className="font-semibold rounded-lg">
                Dashboard
              </Button>
            </Link>
          ) : (
            <>
              <Link href="/login">
                <Button variant="ghost" size="sm" className="font-semibold text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white">
                  Sign In
                </Button>
              </Link>
              <Link href="/register">
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg shadow-sm">
                  Get Started
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
        {renderHeader()}
        <div className="pt-24 p-6 w-full max-w-[1500px] mx-auto space-y-8">
          <Skeleton className="h-6 w-48 mb-4" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <Skeleton className="h-72 w-full rounded-3xl" />
              <Skeleton className="h-48 w-full rounded-3xl" />
            </div>
            <div>
              <Skeleton className="h-96 w-full rounded-3xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !bundle) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col">
        {renderHeader()}
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center pt-24 w-full max-w-[1500px] mx-auto">
          <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mb-4">
            <Package className="w-8 h-8 text-slate-400" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Service Bundle Not Found</h2>
          <p className="text-slate-500 max-w-md mb-6">The medical service bundle you are looking for is unavailable or has been archived.</p>
          <Link href="/">
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl">Return Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      {renderHeader()}

      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 xl:px-10 w-full max-w-[1500px] mx-auto">
        {/* Breadcrumb Navigation & Share Button */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6 text-sm">
          <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 font-medium">
            <Link href="/" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4" />
            {provider?.id ? (
              <Link href={`/providers/${provider.id}`} className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                {provider.name}
              </Link>
            ) : (
              <span>Providers</span>
            )}
            <ChevronRight className="w-4 h-4 text-slate-400" />
            <span className="text-slate-900 dark:text-white font-bold truncate max-w-[200px] sm:max-w-none">{bundle.name}</span>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleCopyLink}
            className="rounded-xl border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800 text-xs font-bold gap-2"
          >
            {copiedLink ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5 text-slate-500" />}
            {copiedLink ? "Link Copied" : "Share Bundle"}
          </Button>
        </div>

        {/* Main Product Page Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Left Column: Product Details & Included Items */}
          <div className="lg:col-span-2 space-y-8">

            {/* Product Overview Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 p-6 md:p-8 shadow-sm relative overflow-hidden"
            >
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-indigo-500 via-cyan-500 to-emerald-500" />

              <div className="flex flex-col md:flex-row gap-6 items-start">
                {/* Bundle Thumbnail or Visual Badge */}
                {bundle.thumbnailUrl ? (
                  <div className="w-full md:w-48 h-48 rounded-2xl overflow-hidden bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex-shrink-0 shadow-md">
                    <img
                      src={getImageUrl(bundle.thumbnailUrl)}
                      alt={bundle.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-24 h-24 md:w-28 md:h-28 rounded-2xl bg-gradient-to-br from-indigo-500/10 to-cyan-500/10 dark:from-indigo-900/30 dark:to-cyan-900/30 border border-indigo-200/50 dark:border-indigo-800/50 flex items-center justify-center flex-shrink-0 shadow-inner">
                    <Package className="w-12 h-12 text-indigo-600 dark:text-indigo-400" />
                  </div>
                )}

                <div className="flex-1 space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    {bundle.isFeatured && (
                      <span className="px-3 py-1 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 font-black text-xs uppercase tracking-wider flex items-center gap-1 border border-amber-500/30 shadow-2xs">
                        <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-500" /> Featured Service
                      </span>
                    )}
                    <span className="px-3 py-1 rounded-full bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 font-bold text-xs uppercase tracking-wider">
                      {bundle.category || "Medical Service Bundle"}
                    </span>
                    {isSubscription ? (
                      <span className="px-3 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 font-extrabold text-xs uppercase tracking-wider flex items-center gap-1 border border-emerald-200 dark:border-emerald-800">
                        <RefreshCw className="w-3.5 h-3.5" /> Subscription Plan
                      </span>
                    ) : discountPercent > 0 ? (
                      <span className="px-3 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 font-extrabold text-xs uppercase tracking-wider flex items-center gap-1">
                        <Sparkles className="w-3.5 h-3.5" /> Save {discountPercent}%
                      </span>
                    ) : null}
                    <span className="px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-semibold text-xs">
                      {bundle.type === "GROUP" ? "Group Package" : "Direct Provider Bundle"}
                    </span>
                  </div>

                  <h1 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight leading-tight">
                    {bundle.name}
                  </h1>

                  <div className="flex flex-wrap items-baseline gap-3 pt-1">
                    {isSubscription ? (
                      <>
                        <span className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
                          ${recurringAmount.toFixed(2)}
                        </span>
                        <span className="text-lg font-extrabold text-emerald-600 dark:text-emerald-400">
                          / {intervalLabel}
                        </span>
                        <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                          (Total Package Value: ${finalPrice.toFixed(2)})
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
                          ${finalPrice.toFixed(2)}
                        </span>
                        {discountPercent > 0 && standardTotal > finalPrice && (
                          <span className="text-lg font-semibold text-slate-400 dark:text-slate-500 line-through decoration-slate-300 dark:decoration-slate-600">
                            ${standardTotal.toFixed(2)}
                          </span>
                        )}
                        <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                          One-Time Payment
                        </span>
                      </>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-4 text-xs font-semibold text-slate-600 dark:text-slate-400 pt-2">
                    <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                      <CheckCircle2 className="w-4 h-4" /> Instant Confirmation
                    </div>
                    <div className="flex items-center gap-1.5">
                      <ShieldCheck className="w-4 h-4 text-indigo-500" /> No Hidden Fees
                    </div>
                    <div className="flex items-center gap-1.5">
                      <UserCheck className="w-4 h-4 text-cyan-500" /> Direct Doctor Appointment
                    </div>
                  </div>
                </div>
              </div>

              {/* Detailed Description */}
              <div className="mt-8 pt-6 border-t border-slate-100 dark:border-slate-800 space-y-3">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-indigo-500" /> Detailed Bundle Description
                </h3>
                <p className="text-slate-600 dark:text-slate-300 leading-relaxed font-medium whitespace-pre-wrap">
                  {bundle.description || "This bundle combines essential diagnostic, consultative, and therapeutic procedure codes into a single discounted package for simplified pricing and comprehensive patient care."}
                </p>
              </div>
            </motion.div>

            {/* What's Included / Included CPT Codes & Custom Items */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 p-6 md:p-8 shadow-sm space-y-6"
            >
              <div className="flex items-center justify-between flex-wrap gap-2 pb-4 border-b border-slate-100 dark:border-slate-800">
                <div>
                  <h3 className="text-xl font-extrabold text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
                    <Package className="w-6 h-6 text-indigo-600 dark:text-indigo-400" /> What's Included in This Bundle
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 font-medium">
                    All listed medical codes and services are covered under the total package price.
                  </p>
                </div>
                <Badge variant="outline" className="font-bold text-xs bg-indigo-50/50 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800">
                  {(bundle.cptCodes?.length || 0) + (bundle.customItems?.length || 0)} Items Covered
                </Badge>
              </div>

              {/* CPT Codes List */}
              {bundle.cptCodes && bundle.cptCodes.length > 0 && (
                <div className="space-y-3">
                  <div className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider flex items-center gap-2">
                    <Stethoscope className="w-4 h-4 text-indigo-500" /> Covered CPT Codes ({bundle.cptCodes.length})
                  </div>
                  <div className="border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-xs">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400">
                        <tr>
                          <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider w-28">CPT Code</th>
                          <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider">Service Description</th>
                          <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-right">Standard Fee</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80">
                        {bundle.cptCodes.map((cpt: any) => {
                          const adjustments = bundle.cptAdjustments || bundle.cptUnits?._adjustments || {};
                          const adj = adjustments[cpt.id] || adjustments[cpt.code];
                          const basePrice = Number(cpt.price || 0);
                          let adjustedPrice = basePrice;
                          let discountLabel = "";

                          if (adj) {
                            if (adj.fixed !== undefined && adj.fixed > 0) {
                              adjustedPrice = Math.max(0, basePrice - adj.fixed);
                              discountLabel = `-$${adj.fixed.toFixed(2)}`;
                            } else if (adj.percent !== undefined && adj.percent > 0) {
                              adjustedPrice = Math.max(0, basePrice * (1 - Math.min(100, adj.percent) / 100));
                              discountLabel = `-${adj.percent}%`;
                            } else if (adj.value > 0) {
                              if (adj.type === "percent") {
                                adjustedPrice = Math.max(0, basePrice * (1 - Math.min(100, adj.value) / 100));
                                discountLabel = `-${adj.value}%`;
                              } else if (adj.type === "fixed") {
                                adjustedPrice = Math.max(0, basePrice - adj.value);
                                discountLabel = `-$${adj.value.toFixed(2)}`;
                              }
                            }
                          }

                          const hasDiscount = adjustedPrice < basePrice;

                          return (
                            <tr key={cpt.id || cpt.code} className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors">
                              <td className="px-4 py-3 font-mono font-bold text-indigo-600 dark:text-indigo-400 text-xs">
                                {cpt.code}
                              </td>
                              <td className="px-4 py-3 text-slate-700 dark:text-slate-200 font-medium text-xs">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span>{cpt.description}</span>
                                  {hasDiscount && (
                                    <span className="px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 text-[10px] font-extrabold uppercase">
                                      {discountLabel} Item Discount
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-slate-900 dark:text-white font-semibold text-xs text-right">
                                {hasDiscount ? (
                                  <div>
                                    <span className="font-bold text-emerald-600 dark:text-emerald-400">${adjustedPrice.toFixed(2)}</span>
                                    <span className="text-[10px] text-slate-400 line-through block font-normal">${basePrice.toFixed(2)}</span>
                                  </div>
                                ) : (
                                  <span>${basePrice.toFixed(2)}</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Custom Extra Items */}
              {bundle.customItems && Array.isArray(bundle.customItems) && bundle.customItems.length > 0 && (
                <div className="space-y-3 pt-2">
                  <div className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-cyan-500" /> Additional Custom Services ({bundle.customItems.length})
                  </div>
                  <div className="border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-xs">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400">
                        <tr>
                          <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider">Item Name</th>
                          <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider">Details</th>
                          <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-right">Price</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80">
                        {bundle.customItems.map((item: any, i: number) => (
                          <tr key={item.id || i} className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors">
                            <td className="px-4 py-3 text-slate-900 dark:text-slate-100 font-bold text-xs">
                              {item.name}
                            </td>
                            <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs font-medium">
                              {item.description || "Included service"}
                            </td>
                            <td className="px-4 py-3 text-slate-900 dark:text-white font-semibold text-xs text-right">
                              ${Number(item.price || 0).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Price Breakdown Card */}
              <div className="bg-slate-50 dark:bg-slate-800/40 rounded-2xl p-5 border border-slate-200/80 dark:border-slate-800 space-y-3">
                <h4 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider">
                  Transparent Price Calculation
                </h4>
                <div className="space-y-2 text-xs font-medium text-slate-600 dark:text-slate-300">
                  <div className="flex justify-between">
                    <span>Combined Standard Service Total</span>
                    <span className="font-bold">${standardTotal.toFixed(2)}</span>
                  </div>
                  {discountPercent > 0 && (
                    <div className="flex justify-between text-emerald-600 dark:text-emerald-400 font-bold">
                      <span>Bundle Package Discount ({discountPercent}%)</span>
                      <span>-${(standardTotal - finalPrice).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-slate-200 dark:border-slate-700 text-sm font-black text-slate-900 dark:text-white">
                    <span>Total Package Out-of-Pocket Rate</span>
                    <span className="text-indigo-600 dark:text-indigo-400">${finalPrice.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Provider Profile Section ("Offered By Provider") */}
            {provider && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 p-6 md:p-8 shadow-sm space-y-6"
              >
                <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-4">
                  <h3 className="text-xl font-extrabold text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
                    <UserCheck className="w-6 h-6 text-indigo-600 dark:text-indigo-400" /> Offered by Healthcare Provider
                  </h3>
                  <Link href={`/providers/${provider.id}`}>
                    <Button variant="ghost" size="sm" className="font-bold text-xs text-indigo-600 hover:text-indigo-700 dark:text-indigo-400">
                      View Full Profile <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                  <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center text-white text-2xl font-black shadow-md flex-shrink-0 ring-4 ring-slate-50 dark:ring-slate-800`}>
                    {initials}
                  </div>

                  <div className="space-y-1.5 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">{provider.name}</h4>
                      {provider.accepting && (
                        <span className="px-2.5 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 font-bold text-[10px] uppercase">
                          Accepting Patients
                        </span>
                      )}
                    </div>

                    <p className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                      {provider.specialty} <span className="text-slate-400">· {provider.yearsExperience} Years Experience</span>
                    </p>

                    <div className="flex items-center gap-4 text-xs text-slate-600 dark:text-slate-400 pt-1 flex-wrap">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                        <span className="font-bold text-slate-900 dark:text-white">{provider.rating ? provider.rating.toFixed(1) : "5.0"}</span>
                        <span>({provider.reviewCount || 12} reviews)</span>
                      </div>
                      {provider.address && (
                        <div className="flex items-center gap-1 text-slate-500">
                          <MapPin className="w-3.5 h-3.5 text-indigo-500" />
                          <span>{provider.address}</span>
                        </div>
                      )}
                      {provider.telehealth && (
                        <div className="flex items-center gap-1 text-indigo-600 dark:text-indigo-400 font-bold">
                          <Video className="w-3.5 h-3.5" />
                          <span>Telehealth Available</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {provider.about && (
                  <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed font-medium bg-slate-50 dark:bg-slate-800/40 p-4 rounded-2xl border border-slate-100 dark:border-slate-800">
                    "{provider.about}"
                  </p>
                )}
              </motion.div>
            )}

            {/* Participating Doctors & Providers Interactive Selection Grid */}
            {availableProviders.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 p-6 md:p-8 shadow-sm space-y-5"
              >
                <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-4">
                  <div>
                    <h3 className="text-xl font-extrabold text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
                      <Stethoscope className="w-6 h-6 text-indigo-600 dark:text-indigo-400" /> Participating Doctors & Providers
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 font-medium">
                      Select a specific doctor for your bundle appointment or let the practice assign any available provider.
                    </p>
                  </div>
                  <Badge variant="outline" className="font-bold text-xs bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800">
                    {availableProviders.length} Doctors
                  </Badge>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Any Available Doctor Card */}
                  {availableProviders.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setSelectedGroupProvider("any")}
                      className={`p-4 rounded-2xl border text-left transition-all flex items-start gap-3.5 ${
                        selectedGroupProvider === "any"
                          ? "border-indigo-600 bg-indigo-50/50 dark:bg-indigo-950/30 dark:border-indigo-500 ring-2 ring-indigo-600/20"
                          : "border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30"
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                        selectedGroupProvider === "any"
                          ? "bg-indigo-600 text-white"
                          : "bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
                      }`}>
                        <UserCheck className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-sm text-slate-900 dark:text-white">Any Available Doctor</span>
                          {selectedGroupProvider === "any" && <Check className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />}
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 font-medium">
                          First available provider at the practice
                        </p>
                      </div>
                    </button>
                  )}

                  {/* Individual Provider Cards */}
                  {availableProviders.map((p) => {
                    const isSelected = selectedGroupProvider === p.id;
                    const doctorInitials = p.name ? p.name.split(" ").filter(Boolean).map(n => n[0]).slice(0, 2).join("") : "DR";
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setSelectedGroupProvider(p.id)}
                        className={`p-4 rounded-2xl border text-left transition-all flex items-start gap-3.5 ${
                          isSelected
                            ? "border-indigo-600 bg-indigo-50/50 dark:bg-indigo-950/30 dark:border-indigo-500 ring-2 ring-indigo-600/20"
                            : "border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30"
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-xs flex-shrink-0 ${
                          isSelected
                            ? "bg-gradient-to-br from-indigo-600 to-cyan-600 text-white"
                            : "bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300"
                        }`}>
                          {doctorInitials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-1">
                            <span className="font-bold text-sm text-slate-900 dark:text-white truncate">{p.name}</span>
                            {isSelected && <Check className="w-4 h-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />}
                          </div>
                          <p className="text-xs text-indigo-600 dark:text-indigo-400 font-medium truncate mt-0.5">
                            {p.specialty || "Medical Specialist"}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}

          </div>

          {/* Right Column: Sticky Action Panel (Checkout & Subscription Options) */}
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="bg-white dark:bg-slate-900 rounded-3xl border-2 border-indigo-600/30 dark:border-indigo-500/40 p-6 shadow-xl sticky top-24 space-y-6"
            >
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                    {isSubscription ? "Subscription Plan" : "One-Time Checkout"}
                  </span>
                  <Badge className={isSubscription ? "bg-emerald-600 text-white font-bold text-[10px]" : "bg-indigo-600 text-white font-bold text-[10px]"}>
                    {isSubscription ? "Recurring Plan" : "Direct Booking"}
                  </Badge>
                </div>
                {isSubscription ? (
                  <div>
                    <h3 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight flex items-baseline gap-1">
                      ${recurringAmount.toFixed(2)} <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">/ {intervalLabel}</span>
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5 font-medium">
                      Total bundle value: ${finalPrice.toFixed(2)}
                    </p>
                  </div>
                ) : (
                  <div>
                    <h3 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                      ${finalPrice.toFixed(2)}
                    </h3>
                    {discountPercent > 0 && standardTotal > finalPrice && (
                      <p className="text-xs text-slate-400 line-through mt-0.5 font-medium">
                        Standard total: ${standardTotal.toFixed(2)}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Subscription / Payment Info Card */}
              {isSubscription ? (
                <div className="bg-emerald-50 dark:bg-emerald-950/40 p-3.5 rounded-2xl border border-emerald-200 dark:border-emerald-800 text-xs font-semibold text-emerald-800 dark:text-emerald-300 space-y-1">
                  <div className="flex items-center gap-1.5 font-bold">
                    <RefreshCw className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /> Subscription Schedule
                  </div>
                  <p className="text-[11px] opacity-90 leading-normal">
                    Billed automatically at <strong>${recurringAmount.toFixed(2)} / {intervalLabel}</strong>
                    {bundle?.billingCycles ? ` for ${bundle.billingCycles} billing cycles` : " ongoing"}. Pause or cancel anytime.
                  </p>
                </div>
              ) : (
                <div className="bg-indigo-50/50 dark:bg-indigo-950/30 p-3.5 rounded-2xl border border-indigo-100 dark:border-indigo-900/50 text-xs font-semibold text-indigo-900 dark:text-indigo-300 space-y-1">
                  <div className="flex items-center gap-1.5 font-bold text-indigo-700 dark:text-indigo-300">
                    <CreditCard className="w-4 h-4 text-indigo-600 dark:text-indigo-400" /> Single One-Time Purchase
                  </div>
                  <p className="text-[11px] opacity-90 leading-normal">
                    One-time payment covering all included services. No recurring fees or ongoing subscription commitments.
                  </p>
                </div>
              )}

              {/* Provider / Doctor Selection Dropdown */}
              {availableProviders.length > 0 && (
                <div className="space-y-1.5 pt-2 border-t border-slate-100 dark:border-slate-800">
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center justify-between">
                    <span>Select Providing Doctor</span>
                    <span className="text-[11px] text-indigo-600 dark:text-indigo-400 font-semibold">
                      {availableProviders.length} Available
                    </span>
                  </label>
                  <Select value={selectedGroupProvider} onValueChange={setSelectedGroupProvider}>
                    <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-800 font-medium text-xs">
                      <SelectValue placeholder="Select Doctor / Provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableProviders.length > 1 && (
                        <SelectItem value="any">Any Available Doctor / Practice Default</SelectItem>
                      )}
                      {availableProviders.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name} {p.specialty ? `(${p.specialty})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Appointment Slot Picker */}
              <div className="space-y-3 pt-2 border-t border-slate-100 dark:border-slate-800">
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center justify-between">
                  <span>Select Appointment Date</span>
                  <span className="text-[11px] text-indigo-600 font-medium">{selectedDate.toLocaleDateString()}</span>
                </label>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-between font-medium rounded-xl border-slate-200 dark:border-slate-800">
                      <span className="flex items-center gap-2">
                        <CalendarIcon className="w-4 h-4 text-indigo-500" />
                        {selectedDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                      </span>
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarUI
                      mode="single"
                      selected={selectedDate}
                      onSelect={(d) => d && setSelectedDate(d)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-slate-500 uppercase">Preferred Time Slot</label>
                  <Select value={selectedTime} onValueChange={setSelectedTime}>
                    <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-800">
                      <SelectValue placeholder="Select Time Slot" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="09:00 AM">09:00 AM</SelectItem>
                      <SelectItem value="10:30 AM">10:30 AM</SelectItem>
                      <SelectItem value="01:00 PM">01:00 PM</SelectItem>
                      <SelectItem value="02:30 PM">02:30 PM</SelectItem>
                      <SelectItem value="04:00 PM">04:00 PM</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Patient Notes input */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-slate-500 uppercase">Optional Notes / Reason for Visit</label>
                <textarea
                  value={patientNotes}
                  onChange={(e) => setPatientNotes(e.target.value)}
                  placeholder="Mention symptoms, specific requests, or medical history..."
                  className="w-full p-3 text-xs font-medium rounded-xl border border-slate-200 dark:border-slate-800 bg-transparent focus:ring-2 focus:ring-indigo-500 outline-none resize-none h-16"
                />
              </div>

              {/* Primary Action Button */}
              <Button
                onClick={handleCheckout}
                disabled={isSubmitting}
                className={`w-full py-6 rounded-2xl text-white font-extrabold text-base shadow-lg transition-all hover:scale-[1.01] ${
                  isSubscription 
                    ? "bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/30" 
                    : "bg-indigo-600 hover:bg-indigo-700 shadow-indigo-600/30"
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" /> Processing...
                  </span>
                ) : isSubscription ? (
                  `Subscribe Now - $${recurringAmount.toFixed(2)} / ${intervalLabel}`
                ) : (
                  `Checkout Now - $${finalPrice.toFixed(2)}`
                )}
              </Button>

              {/* Trust Badges */}
              <div className="space-y-2 pt-2 border-t border-slate-100 dark:border-slate-800 text-[11px] text-slate-500 dark:text-slate-400 font-medium">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-emerald-500" />
                  <span>Encrypted 256-Bit SSL Checkout</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-indigo-500" />
                  <span>Verified Medical Board Certified Practice</span>
                </div>
                <div className="flex items-center gap-2">
                  <Info className="w-4 h-4 text-cyan-500" />
                  <span>Upfront pricing guaranteed with no surprise bills</span>
                </div>
              </div>
            </motion.div>
          </div>

        </div>
      </main>

      {/* Guest Auth Dialog */}
      <Dialog open={showAuthModal} onOpenChange={setShowAuthModal}>
        <DialogContent className="sm:max-w-md rounded-3xl p-6">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
              Sign In or Register to Checkout
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-sm font-medium pt-1">
              Create a free StraightDoc member account or sign in to complete your purchase of <strong>{bundle.name}</strong> (${finalPrice.toFixed(2)}).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-4">
            <Link href={`/login?redirect=${encodeURIComponent(window.location.pathname)}`}>
              <Button className="w-full py-5 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm">
                Sign In to Account
              </Button>
            </Link>

            <Link href={`/member/register?bundleId=${bundle.id}`}>
              <Button variant="outline" className="w-full py-5 rounded-2xl border-slate-200 dark:border-slate-800 font-bold text-sm">
                Create Free Member Account
              </Button>
            </Link>
          </div>
        </DialogContent>
      </Dialog>

      {/* Booking Success Dialog */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="sm:max-w-md rounded-3xl p-6 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto mb-3 shadow-inner">
            <CheckCircle2 className="w-10 h-10" />
          </div>
          <DialogHeader>
            <DialogTitle className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
              {isSubscription ? "Subscription Order Submitted!" : "Bundle Order Submitted!"}
            </DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-300 text-sm font-medium pt-2">
              Your appointment request for <strong>{bundle.name}</strong> on {selectedDate.toLocaleDateString()} at {selectedTime} has been submitted successfully to <strong>{provider?.name || "the provider"}</strong>.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="sm:justify-center mt-4">
            <Link href="/member/appointments">
              <Button className="rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-6">
                View My Appointments
              </Button>
            </Link>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
